

























































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
} from '@vue/composition-api';
import { Size, Spacing } from '@front-app/components/constants';
import {
	EventCurrency,
	EventManagementData,
	EventPrice,
	NonValidEventPrice,
	PriceOption,
} from '@front-app/services/EventsApi/EventModel';
import { v4 } from 'uuid';
import { v } from '@front-app/components/form/validation/useValidation';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { syncedRef } from '@front-app/util/reactivity';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import TrCheckbox from '@front-app/components/inputs/Checkbox.vue';
import RadioList from '@front-app/components/inputs/RadioList.vue';
import FormError from '@front-app/components/form/FormError.vue';
import EditEventBaseForm from './EditEventBaseForm.vue';
import { useEventAdminStore } from '../../store/EventAdmin';
import { eventPriceValidator, isValidEventPrice, numericValueValidator } from '../../util/validation';

type PriceOptionSelectChoice = Exclude<PriceOption, PriceOption.NONE>;

const EVENT_PRICE_TYPE_OPTIONS: PriceOptionSelectChoice[] = [
	PriceOption.FREE,
	PriceOption.TBD,
	PriceOption.VALUE,
];

const EVENT_PRICE_TYPE_OPTION_LABELS: Record<PriceOptionSelectChoice, string> = {
	[PriceOption.FREE]: 'price.free',
	[PriceOption.TBD]: 'price.show-as-tbd',
	[PriceOption.VALUE]: 'price.set-value',
};

const EVENT_PRICE_CURRENCY_OPTIONS = [
	EventCurrency.CAD,
	EventCurrency.USD,
];

const EVENT_PRICE_CURRENCY_OPTION_LABELS: Record<EventCurrency, string> = {
	[EventCurrency.CAD]: 'CAD',
	[EventCurrency.USD]: 'USD',
};

export default defineComponent({
	components: {
		EditEventBaseForm,
		FormSection,
		FormLabel,
		FormInput,
		TrCheckbox,
		RadioList,
		FormError,
	},
	props: {
		eventData: {
			type: Object as PropType<EventManagementData>,
			required: true,
		},
	},
	setup(props, ctx) {
		// Pricing
		const eventPrice: Ref<EventPrice | NonValidEventPrice> = syncedRef(() => props.eventData.price).ref;
		const eventPriceTypeOptions = EVENT_PRICE_TYPE_OPTIONS.map((key) => ({
			key,
			label: EVENT_PRICE_TYPE_OPTION_LABELS[key],
		}));
		const eventPriceCurrencyOptions = EVENT_PRICE_CURRENCY_OPTIONS.map((key) => ({
			key,
			label: EVENT_PRICE_CURRENCY_OPTION_LABELS[key],
		}));
		const toggleEventPrice = () => {
			if (eventPrice.value.type === PriceOption.NONE) {
				eventPrice.value.type = PriceOption.VALUE;
				eventPrice.value.numericValue = 0;
				eventPrice.value.currency = EventCurrency.CAD;
			} else {
				eventPrice.value.type = PriceOption.NONE;
			}
		};
		const setEventPriceNumeric = (inputVal: string) => {
			if (numericValueValidator.validator(inputVal)) {
				const parsedInput = parseFloat(inputVal);
				Object.assign(eventPrice.value, {
					...eventPrice.value,
					numericValue: parsedInput,
					value: `${parsedInput} ${eventPrice.value.currency}`,
				});
			} else {
				Object.assign(eventPrice.value, {
					...eventPrice.value,
					type: PriceOption.VALUE,
					numericValue: inputVal,
				});
			}
		};

		const updateCurrency = (currency: EventCurrency) => {
			Object.assign(eventPrice.value, {
				...eventPrice.value,
				currency,
				value: `${eventPrice.value.numericValue} ${currency}`,
			});
		};

		const {
			track,
			isLoading: isSavingUpdates,
			isError: isErrorSavingUpdates,
			isSuccess: isSuccessSavingUpdates,
		} = useAsyncStatus();

		const validation = v({
			eventPrice: {
				value: computed(() => eventPrice),
				rules: [eventPriceValidator],
			},
		});

		const formId = `event-form-${v4()}`;
		const { focusFirstInvalid } = useFocusFirstInvalidField({ containerSelector: `#${formId}` });

		const saveUpdates = async () => {
			validation.validate();
			const price = eventPrice.value;
			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}

			// This shouldn't be needed as it is included in the validation above,
			// but this helps satisfy TS
			if (!isValidEventPrice(price)) {
				validation.fields.eventPrice.isValid = false;
				focusFirstInvalid();
				return;
			}

			await track(useEventAdminStore().update(props.eventData.id, { price }));

			if (isSuccessSavingUpdates.value) {
				ctx.emit('close');
			}
		};

		return {
			eventPrice,
			eventPriceTypeOptions,
			eventPriceCurrencyOptions,
			PriceOption,
			toggleEventPrice,
			setEventPriceNumeric,
			updateCurrency,
			EVENT_PRICE_TYPE_OPTION_LABELS,
			EVENT_PRICE_CURRENCY_OPTION_LABELS,
			// Saving
			formId,
			validation,
			isSavingUpdates,
			isErrorSavingUpdates,
			saveUpdates,
			// Util
			Size,
			Spacing,
			ButtonPalettes,
			DropdownPosition,
		};
	},
});
